import { useRef, useMemo, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import ReactGantt, { ReactGanttRef } from "@dhx/react-gantt";
import "@dhx/react-gantt/dist/react-gantt.css";

import { useTheme } from "../../../shared/context";
import {
  transformProjectsToGanttTasks,
  ProjectForGantt,
} from "../utils/ganttTransformers";

export type ZoomLevel = "day" | "week" | "month" | "year";

interface ProjectsGanttViewProps {
  projects: ProjectForGantt[];
  zoomLevel?: ZoomLevel;
  onProjectClick?: (projectId: number, projectName: string) => void;
}

const getScalesForZoom = (zoom: ZoomLevel) => {
  switch (zoom) {
    case "day":
      return [
        { unit: "month", step: 1, format: "%M %Y" },
        { unit: "day", step: 1, format: "%d %M" },
      ];
    case "week":
      return [
        { unit: "month", step: 1, format: "%M %Y" },
        { unit: "week", step: 1, format: "Wk #%W" },
      ];
    case "month":
      return [
        { unit: "year", step: 1, format: "%Y" },
        { unit: "month", step: 1, format: "%M %Y" },
      ];
    case "year":
      return [{ unit: "year", step: 1, format: "%Y" }];
  }
};

export const ProjectsGanttView: React.FC<ProjectsGanttViewProps> = ({
  projects,
  zoomLevel = "month",
  onProjectClick,
}) => {
  const { mode } = useTheme();
  const ganttRef = useRef<ReactGanttRef>(null);

  const tasks = useMemo(
    () => transformProjectsToGanttTasks(projects),
    [projects]
  );

  const ganttTheme = mode === "dark" ? "dark" : "terrace";

  useEffect(() => {
    const ganttInstance = ganttRef.current?.instance;
    if (!ganttInstance) return;

    ganttInstance.config.scales = getScalesForZoom(zoomLevel) as any;
    ganttInstance.render();
  }, [zoomLevel]);

  useEffect(() => {
    const ganttInstance = ganttRef.current?.instance;
    if (!ganttInstance || !onProjectClick) return;

    const handleGridClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const iconEl = target.closest(".gantt-view-icon");
      if (iconEl) {
        const projectIdAttr = iconEl.getAttribute("data-project-id");
        if (projectIdAttr?.startsWith("project_")) {
          const projectId = Number(projectIdAttr.replace("project_", ""));
          const project = projects.find((p) => p.id === projectId);
          if (project) {
            onProjectClick(projectId, project.title);
          }
        }
      }
    };

    const gridElement = ganttInstance.$grid;
    gridElement?.addEventListener("click", handleGridClick);

    return () => {
      gridElement?.removeEventListener("click", handleGridClick);
    };
  }, [onProjectClick]);

  if (tasks.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Typography color="text.secondary">
          No projects with schedule data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" sx={{ "& .gantt_container": { height: "100%" } }}>
      <ReactGantt
        ref={ganttRef}
        tasks={tasks}
        links={[]}
        config={{
          scales: [
            { unit: "month", step: 1, format: "%M %Y" },
            { unit: "day", step: 1, format: "%d %M" },
          ],
          columns: [
            { name: "text", label: "Project", tree: true, width: 200 },
            {
              name: "start_date",
              label: "Planned Start Date",
              align: "center",
              width: 130,
            },
            { name: "duration", label: "Duration", align: "center", width: 60 },
            {
              name: "view",
              label: "",
              align: "center",
              width: 40,
              template: (task: { id: string }) => {
                if (String(task.id).startsWith("project_")) {
                  return `<span class="gantt-view-icon" data-project-id="${task.id}" style="cursor:pointer;display:inline-flex;align-items:center;justify-content:center;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </span>`;
                }
                return "";
              },
            },
          ],
          scale_height: 60,
          grid_width: 520,
          drag_progress: false,
          drag_resize: false,
          drag_move: false,
          order_branch: false,
          fit_tasks: true,
          show_empty_state: true,
          readonly: true,
        }}
        plugins={{ tooltip: true }}
        theme={ganttTheme}
        onBeforeLightbox={() => false}
      />
    </Box>
  );
};
